import React, { useState } from "react";
import { useSite } from "../../context/SiteContext";
import { ArrowUpRight, X, Play } from "lucide-react";

const ProjectModal = ({ project, onClose }) => {
  const [activeVideo, setActiveVideo] = useState(
    project.videos && project.videos.length > 0 ? project.videos[0] : null
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center overflow-y-auto bg-black/85 backdrop-blur-sm p-4 md:p-10"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl bg-[#0f0f0f] border border-zinc-800 my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 md:-top-4 md:-right-4 w-9 h-9 md:w-10 md:h-10 rounded-full bg-stone-100 text-zinc-950 flex items-center justify-center hover:bg-amber-300 transition-colors z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Media */}
        <div className="aspect-video bg-black relative">
          {activeVideo ? (
            <iframe
              key={activeVideo.id}
              className="absolute inset-0 w-full h-full"
              src={
                activeVideo.provider === "vimeo"
                  ? `https://player.vimeo.com/video/${activeVideo.id}?${
                      activeVideo.h ? `h=${activeVideo.h}&` : ""
                    }title=0&byline=0&portrait=0`
                  : `https://www.youtube.com/embed/${activeVideo.id}?rel=0&modestbranding=1`
              }
              title={activeVideo.title}
              frameBorder="0"
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <img
              src={project.cover}
              alt={project.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </div>

        <div className="p-6 md:p-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {project.categories.map((c) => (
              <span
                key={c}
                className="text-[10px] uppercase tracking-[0.22em] text-stone-400 border border-zinc-800 px-2.5 py-1"
              >
                {c}
              </span>
            ))}
            <span className="text-[11px] uppercase tracking-[0.22em] text-stone-500 ml-auto">
              {project.year}
            </span>
          </div>

          <h3 className="font-display text-4xl md:text-5xl text-stone-50 leading-tight">
            {project.title}
          </h3>
          <p className="mt-3 text-stone-400 text-sm">
            <span className="text-amber-300">{project.client}</span> · {project.role}
          </p>

          <p className="mt-6 text-stone-300 text-[15px] md:text-base leading-[1.75] max-w-3xl">
            {project.summary}
          </p>

          {project.impact && project.impact.length > 0 && (
            <div className="mt-8 border-t border-zinc-800 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-300/80 mb-3">
                Outcomes
              </p>
              <ul className="space-y-2 text-stone-300 text-[15px]">
                {project.impact.map((b, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-amber-300 mt-0.5">—</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {project.pullQuote && (
            <blockquote className="mt-8 pl-6 border-l-2 border-amber-300/70 font-display italic text-2xl md:text-3xl text-stone-100 leading-snug">
              "{project.pullQuote}"
            </blockquote>
          )}

          {project.links && project.links.length > 0 && (
            <div className="mt-8 border-t border-zinc-800 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-stone-500 mb-3">
                Press &amp; Links
              </p>
              <ul className="space-y-2">
                {project.links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noreferrer"
                      className="group inline-flex items-center gap-2 text-stone-300 hover:text-amber-300 transition-colors"
                    >
                      <span className="link-anim">{l.label}</span>
                      <ArrowUpRight
                        size={14}
                        className="opacity-60 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {project.videos && project.videos.length > 1 && (
            <div className="mt-8 border-t border-zinc-800 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-stone-500 mb-3">
                More from this project
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {project.videos.map((v) => {
                  const active = activeVideo && activeVideo.id === v.id;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setActiveVideo(v)}
                      className={`text-left group p-3 border transition-colors ${
                        active
                          ? "border-amber-300/70 bg-amber-300/5"
                          : "border-zinc-800 hover:border-stone-500"
                      }`}
                    >
                      <div className="flex gap-3 items-center">
                        <div className="relative w-20 h-12 flex-shrink-0 overflow-hidden bg-black">
                          <img
                            src={
                              v.provider === "vimeo"
                                ? `https://vumbnail.com/${v.id}.jpg`
                                : `https://i.ytimg.com/vi/${v.id}/mqdefault.jpg`
                            }
                            alt={v.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = project.cover;
                            }}
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/10 transition-colors">
                            <Play size={14} className="text-white fill-current" />
                          </span>
                        </div>
                        <span
                          className={`text-[13px] leading-snug ${
                            active ? "text-stone-50" : "text-stone-300"
                          }`}
                        >
                          {v.title}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Work = () => {
  const { projects } = useSite();
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      className="relative bg-[#0a0a0a] py-24 md:py-36 border-t border-zinc-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-14 md:mb-20">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300/90 mb-4">
              02 — Selected Work
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-stone-50 leading-tight">
              Nine stories, <span className="italic">one producer.</span>
            </h2>
          </div>
          <p className="hidden md:block text-stone-500 text-sm max-w-xs">
            A cross-section of a decade — Web3 studios, fintech launches, festival coverage and brand documentary.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900">
          {projects.map((p, i) => (
            <button
              key={p.slug}
              onClick={() => setActive(p)}
              className="project-card group relative bg-[#0a0a0a] text-left overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="project-img w-full h-full object-cover opacity-90 group-hover:opacity-100"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent pointer-events-none" />
              </div>

              <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-stone-300 font-mono">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.22em] text-stone-400 font-mono">
                {p.year}
              </div>

              <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl text-stone-50 leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-[12px] uppercase tracking-[0.2em] text-stone-400">
                      {p.categories.slice(0, 2).join(" · ")}
                    </p>
                  </div>
                  <span className="w-10 h-10 rounded-full border border-stone-400/60 flex items-center justify-center text-stone-200 group-hover:bg-amber-300 group-hover:border-amber-300 group-hover:text-zinc-950 transition-all">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && <ProjectModal project={active} onClose={() => setActive(null)} />}
    </section>
  );
};

export default Work;
